import { useEffect, useState } from "react";
import AppShell from "@/components/AppShell";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { FileCog, ChevronLeft, Pencil, Trash2, Plus, AlertTriangle, Save, Flag, Inbox, Check, GraduationCap } from "lucide-react";

const EMPTY = { subtes: "", question: "", options: ["", "", "", ""], answer_index: 0, pembahasan: "", tingkat: "sedang" };

export default function AdminSoal() {
  const { user } = useAuth();
  const [packages, setPackages] = useState([]);
  const [pid, setPid] = useState(null);
  const [pkg, setPkg] = useState(null);
  const [editing, setEditing] = useState(null); // qid or "new"
  const [form, setForm] = useState(EMPTY);
  const [reports, setReports] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [cform, setCform] = useState({ ptn: "", prodi: "", kelompok: "", target: 75 });

  const loadReports = () => api.get("/admin/question-reports").then((r) => setReports(r.data.reports)).catch(() => {});
  const loadCatalog = () => api.get("/admin/prodi-catalog").then((r) => setCatalog(r.data.catalog)).catch(() => {});

  useEffect(() => {
    if (user?.role !== "admin") return;
    api.get("/admin/tryout-bank").then((r) => setPackages(r.data.packages)).catch(() => {});
    loadReports();
    loadCatalog();
    loadPromo();
  }, [user]);

  const resolveReport = async (rid) => { await api.post(`/admin/question-reports/${rid}/resolve`); toast.success("Laporan ditandai selesai"); loadReports(); };
  const deleteReport = async (rid) => { await api.delete(`/admin/question-reports/${rid}`); toast.success("Laporan dihapus"); loadReports(); };
  const addProdi = async () => {
    if (!cform.ptn.trim() || !cform.prodi.trim()) { toast.error("Isi PTN & prodi dulu"); return; }
    await api.post("/admin/prodi-catalog", cform);
    toast.success("Prodi ditambahkan"); setCform({ ptn: "", prodi: "", kelompok: "", target: 75 }); loadCatalog();
  };
  const delProdi = async (cid) => { await api.delete(`/admin/prodi-catalog/${cid}`); toast.success("Prodi dihapus"); loadCatalog(); };
  const [promo, setPromo] = useState({ active: false, expires_at: "" });
  const loadPromo = () => api.get("/admin/promo").then((r) => setPromo(r.data.promo || { active: false, expires_at: "" })).catch(() => {});
  const savePromo = async () => { await api.post("/admin/promo", { active: promo.active, expires_at: promo.expires_at }); toast.success("Promo disimpan"); loadPromo(); };
  const autoTag = async () => {
    try { await api.post(`/admin/tryout-bank/${pid}/auto-tag`); toast.success("AI mulai menandai tingkat kesulitan. Muat ulang paket ini beberapa saat lagi."); }
    catch { toast.error("Gagal memulai auto-tag"); }
  };

  const openPkg = (id) => {
    setPid(id); setEditing(null);
    api.get(`/admin/tryout-bank/${id}`).then((r) => setPkg(r.data.package)).catch(() => {});
  };

  const startEdit = (q) => { setEditing(q.id); setForm({ ...q }); };
  const startAdd = (subtes) => { setEditing("new"); setForm({ ...EMPTY, subtes }); };

  const save = async () => {
    if (form.options.some((o) => !o.trim())) { toast.error("Semua opsi harus diisi"); return; }
    try {
      if (editing === "new") await api.post(`/admin/tryout-bank/${pid}/question`, form);
      else await api.put(`/admin/tryout-bank/${pid}/question/${editing}`, form);
      toast.success("Soal tersimpan");
      setEditing(null); openPkg(pid);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Gagal menyimpan"); }
  };

  const del = async (qid) => {
    await api.delete(`/admin/tryout-bank/${pid}/question/${qid}`);
    toast.success("Soal dihapus"); openPkg(pid);
  };

  const dup = (opts) => new Set(opts.map((o) => o.trim())).size !== opts.length;

  if (user?.role !== "admin")
    return <AppShell><p className="text-[#5C5F5C]">Halaman ini khusus admin.</p></AppShell>;

  // Question editor form
  const Editor = (
    <div className="clay-card p-6 mb-4 border-[#E25E3E]/40" data-testid="question-editor">
      <div className="grid gap-3">
        <label className="text-sm font-medium text-[#1A1C1A]">Subtes
          <input value={form.subtes} onChange={(e) => setForm({ ...form, subtes: e.target.value })} className="mt-1 w-full px-3 py-2 rounded-lg border border-[#E5E0D8] outline-none focus:border-[#E25E3E]" data-testid="edit-subtes" /></label>
        <label className="text-sm font-medium text-[#1A1C1A]">Pertanyaan
          <textarea value={form.question} onChange={(e) => setForm({ ...form, question: e.target.value })} rows={2} className="mt-1 w-full px-3 py-2 rounded-lg border border-[#E5E0D8] outline-none focus:border-[#E25E3E]" data-testid="edit-question" /></label>
        <div className="space-y-2">
          <p className="text-sm font-medium text-[#1A1C1A]">Opsi (pilih yang benar)</p>
          {dup(form.options) && <p className="text-xs text-[#D9381E] flex items-center gap-1"><AlertTriangle className="h-3.5 w-3.5" /> Ada opsi bernilai/teks sama — perbaiki agar tidak ganda.</p>}
          {form.options.map((o, i) => (
            <div key={i} className="flex items-center gap-2">
              <input type="radio" name="benar" checked={form.answer_index === i} onChange={() => setForm({ ...form, answer_index: i })} data-testid={`edit-correct-${i}`} className="accent-[#E25E3E] h-4 w-4" />
              <input value={o} onChange={(e) => { const op = [...form.options]; op[i] = e.target.value; setForm({ ...form, options: op }); }} className="flex-1 px-3 py-2 rounded-lg border border-[#E5E0D8] outline-none focus:border-[#E25E3E]" data-testid={`edit-option-${i}`} />
            </div>
          ))}
        </div>
        <label className="text-sm font-medium text-[#1A1C1A]">Pembahasan
          <input value={form.pembahasan} onChange={(e) => setForm({ ...form, pembahasan: e.target.value })} className="mt-1 w-full px-3 py-2 rounded-lg border border-[#E5E0D8] outline-none focus:border-[#E25E3E]" data-testid="edit-pembahasan" /></label>
        <label className="text-sm font-medium text-[#1A1C1A]">Tingkat Kesulitan (IRT-ringan)
          <select value={form.tingkat || "sedang"} onChange={(e) => setForm({ ...form, tingkat: e.target.value })} data-testid="edit-tingkat" className="mt-1 w-full px-3 py-2 rounded-lg border border-[#E5E0D8] outline-none focus:border-[#E25E3E]">
            <option value="mudah">Mudah (bobot 1)</option>
            <option value="sedang">Sedang (bobot 2)</option>
            <option value="sulit">Sulit (bobot 3)</option>
          </select></label>
        <div className="flex gap-2">
          <button onClick={save} data-testid="save-question-btn" className="px-5 py-2.5 rounded-full bg-[#E25E3E] text-white text-sm font-medium hover:bg-[#C94F31] flex items-center gap-1.5"><Save className="h-4 w-4" /> Simpan</button>
          <button onClick={() => setEditing(null)} className="px-5 py-2.5 rounded-full border-2 border-[#1A1C1A] text-sm font-medium hover:bg-[#1A1C1A]/5">Batal</button>
        </div>
      </div>
    </div>
  );

  // Package list
  if (!pid || !pkg) {
    return (
      <AppShell>
        <div className="mb-6 flex items-center gap-2"><FileCog className="h-6 w-6 text-[#E25E3E]" /><h1 className="font-heading font-black text-3xl text-[#1A1C1A]">Kelola Soal TryOut</h1></div>
        <p className="text-[#5C5F5C] mb-6">Pilih paket untuk meninjau, memperbaiki, menambah, atau menghapus soal.</p>

        {(() => { const open = reports.filter((r) => r.status !== "selesai"); return (
          <div className="clay-card p-6 mb-8" data-testid="admin-reports-panel">
            <div className="flex items-center gap-2 mb-4">
              <Flag className="h-5 w-5 text-[#D9381E]" />
              <h2 className="font-heading font-bold text-xl text-[#1A1C1A]">Laporan Soal dari Siswa</h2>
              {open.length > 0 && <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-[#D9381E] text-white" data-testid="reports-badge">{open.length} baru</span>}
            </div>
            {reports.length === 0 ? (
              <p className="text-sm text-[#5C5F5C] flex items-center gap-2"><Inbox className="h-4 w-4" /> Belum ada laporan soal.</p>
            ) : (
              <div className="space-y-3">
                {reports.map((r) => (
                  <div key={r.id} data-testid={`report-${r.id}`} className={`rounded-xl border p-4 ${r.status === "selesai" ? "border-[#E5E0D8] bg-[#FAF8F5] opacity-70" : "border-[#D9381E]/30 bg-[#D9381E]/5"}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-xs text-[#5C5F5C] mb-1">{r.tryout_id} · {r.user_name || r.user_email} · {new Date(r.created_at).toLocaleString("id-ID")}</p>
                        {r.question_text && <p className="text-sm font-medium text-[#1A1C1A] line-clamp-2">Soal: {r.question_text}</p>}
                        <p className="text-sm text-[#D9381E] mt-1"><b>Masalah:</b> {r.reason}</p>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        {r.status !== "selesai" && <button onClick={() => resolveReport(r.id)} data-testid={`resolve-report-${r.id}`} className="p-2 rounded-full hover:bg-[#4A6B53]/10 text-[#5C5F5C] hover:text-[#4A6B53]" title="Tandai selesai"><Check className="h-4 w-4" /></button>}
                        <button onClick={() => deleteReport(r.id)} data-testid={`delete-report-${r.id}`} className="p-2 rounded-full hover:bg-[#D9381E]/10 text-[#5C5F5C] hover:text-[#D9381E]" title="Hapus"><Trash2 className="h-4 w-4" /></button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ); })()}

        <div className="clay-card p-6 mb-8" data-testid="admin-prodi-panel">
          <div className="flex items-center gap-2 mb-1"><GraduationCap className="h-5 w-5 text-[#E25E3E]" /><h2 className="font-heading font-bold text-xl text-[#1A1C1A]">Katalog Target Prodi (estimasi)</h2></div>
          <p className="text-xs text-[#5C5F5C] mb-4">Angka target = estimasi keketatan (target % benar di TryOut), <b>bukan</b> passing grade resmi. Siswa memilih maks 4 dari katalog ini.</p>
          <div className="grid sm:grid-cols-4 gap-2 mb-4">
            <input value={cform.ptn} onChange={(e) => setCform({ ...cform, ptn: e.target.value })} data-testid="cat-ptn" placeholder="PTN" className="px-3 py-2 rounded-lg border border-[#E5E0D8] outline-none focus:border-[#E25E3E] text-sm" />
            <input value={cform.prodi} onChange={(e) => setCform({ ...cform, prodi: e.target.value })} data-testid="cat-prodi" placeholder="Prodi" className="px-3 py-2 rounded-lg border border-[#E5E0D8] outline-none focus:border-[#E25E3E] text-sm" />
            <select value={cform.kelompok} onChange={(e) => setCform({ ...cform, kelompok: e.target.value })} data-testid="cat-kelompok" className="px-3 py-2 rounded-lg border border-[#E5E0D8] outline-none focus:border-[#E25E3E] text-sm"><option value="">Kelompok</option><option>Saintek</option><option>Soshum</option></select>
            <div className="flex gap-2"><input type="number" min={0} max={100} value={cform.target} onChange={(e) => setCform({ ...cform, target: Number(e.target.value) })} data-testid="cat-target" placeholder="Target%" className="w-24 px-3 py-2 rounded-lg border border-[#E5E0D8] outline-none focus:border-[#E25E3E] text-sm" /><button onClick={addProdi} data-testid="add-prodi-btn" className="px-3 py-2 rounded-full bg-[#1A1C1A] text-white text-sm flex items-center gap-1 hover:opacity-90"><Plus className="h-4 w-4" /> Tambah</button></div>
          </div>
          <div className="max-h-64 overflow-y-auto divide-y divide-[#E5E0D8] border border-[#E5E0D8] rounded-lg">
            {catalog.map((p) => (
              <div key={p.id} data-testid={`cat-row-${p.id}`} className="flex items-center justify-between px-3 py-2">
                <span className="text-sm text-[#1A1C1A]">{p.prodi} <span className="text-xs text-[#5C5F5C]">· {p.ptn} · {p.kelompok || "-"}</span></span>
                <div className="flex items-center gap-2"><span className="text-xs font-medium text-[#E25E3E]">±{p.target}</span><button onClick={() => delProdi(p.id)} data-testid={`del-prodi-${p.id}`} className="p-1.5 rounded-full text-[#5C5F5C] hover:text-[#D9381E] hover:bg-[#D9381E]/10"><Trash2 className="h-4 w-4" /></button></div>
              </div>
            ))}
            {catalog.length === 0 && <p className="text-sm text-[#5C5F5C] px-3 py-2">Belum ada katalog.</p>}
          </div>
        </div>

        <div className="clay-card p-6 mb-8" data-testid="admin-promo-panel">
          <div className="flex items-center gap-2 mb-1"><Flag className="h-5 w-5 text-[#E25E3E]" /><h2 className="font-heading font-bold text-xl text-[#1A1C1A]">Promo TryOut Gratis</h2></div>
          <p className="text-xs text-[#5C5F5C] mb-4">Saat aktif, setiap siswa dapat <b>1x TryOut gratis sesuai jenjang</b> (SMP kls 9 → TKA SMP; SMA → UTBK), berlaku sampai waktu berakhir. Otomatis untuk siswa lama & baru.</p>
          <label className="flex items-center gap-2 text-sm text-[#1A1C1A] mb-3">
            <input type="checkbox" checked={!!promo.active} onChange={(e) => setPromo({ ...promo, active: e.target.checked })} data-testid="promo-active" className="h-4 w-4" /> Promo aktif
          </label>
          <label className="text-sm font-medium text-[#1A1C1A] block mb-1">Berakhir pada (UTC)</label>
          <input value={promo.expires_at || ""} onChange={(e) => setPromo({ ...promo, expires_at: e.target.value })} data-testid="promo-expiry" placeholder="2026-08-24T16:00:00+00:00" className="w-full sm:w-96 px-3 py-2 rounded-lg border border-[#E5E0D8] outline-none focus:border-[#E25E3E] text-sm" />
          <p className="text-[11px] text-[#5C5F5C] mt-1">Format UTC. Contoh: 24 Agu 2026 pukul 23:00 WIB = <b>2026-08-24T16:00:00+00:00</b>.</p>
          <button onClick={savePromo} data-testid="save-promo-btn" className="mt-4 px-5 py-2.5 rounded-full bg-[#E25E3E] text-white text-sm font-medium hover:bg-[#C94F31] flex items-center gap-1.5"><Save className="h-4 w-4" /> Simpan Promo</button>
        </div>

        <div className="grid md:grid-cols-2 gap-5">
          {packages.map((p) => (
            <button key={p.id} onClick={() => openPkg(p.id)} data-testid={`admin-pkg-${p.id}`} className="clay-card clay-card-hover p-6 text-left">
              <span className={`text-xs font-bold px-3 py-1.5 rounded-full ${p.kategori === "UTBK" ? "bg-[#4A6B53] text-white" : "bg-[#F4B942] text-[#1A1C1A]"}`}>{p.kategori}</span>
              <h3 className="font-heading font-bold text-lg text-[#1A1C1A] mt-3">{p.nama}</h3>
              <p className="text-sm text-[#5C5F5C] mt-1">{p.jumlah} soal · {p.subtes.length} subtes</p>
            </button>
          ))}
          {packages.length === 0 && <p className="text-[#5C5F5C]">Belum ada bank soal.</p>}
        </div>
      </AppShell>
    );
  }

  const grouped = {};
  (pkg.soal || []).forEach((q) => { (grouped[q.subtes] = grouped[q.subtes] || []).push(q); });

  return (
    <AppShell>
      <button onClick={() => { setPid(null); setPkg(null); }} className="text-sm text-[#5C5F5C] flex items-center gap-1.5 mb-3 hover:text-[#1A1C1A]"><ChevronLeft className="h-4 w-4" /> Semua Paket</button>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-1">
        <h1 className="font-heading font-black text-2xl text-[#1A1C1A]">{pkg.nama}</h1>
        <button onClick={autoTag} data-testid="auto-tag-btn" className="text-sm px-4 py-2 rounded-full bg-[#4A6B53] text-white font-medium hover:opacity-90 flex items-center gap-1.5"><GraduationCap className="h-4 w-4" /> Auto-tag Kesulitan (AI)</button>
      </div>
      <p className="text-[#5C5F5C] mb-6">{(pkg.soal || []).length} soal</p>

      {editing === "new" && Editor}

      {Object.entries(grouped).map(([subtes, qs]) => (
        <div key={subtes} className="mb-8">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-heading font-bold text-lg text-[#1A1C1A]">{subtes} <span className="text-sm font-normal text-[#5C5F5C]">({qs.length})</span></h2>
            <button onClick={() => startAdd(subtes)} data-testid={`add-q-${subtes}`} className="text-sm px-3 py-1.5 rounded-full bg-[#1A1C1A] text-white flex items-center gap-1 hover:opacity-90"><Plus className="h-3.5 w-3.5" /> Tambah</button>
          </div>
          <div className="space-y-3">
            {qs.map((q, i) => editing === q.id ? <div key={q.id}>{Editor}</div> : (
              <div key={q.id} className="clay-card p-4" data-testid={`admin-q-${q.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-[#1A1C1A]">{i + 1}. {q.question} <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${q.tingkat === "sulit" ? "bg-[#D9381E]/10 text-[#D9381E]" : q.tingkat === "mudah" ? "bg-[#4A6B53]/10 text-[#4A6B53]" : "bg-[#F4B942]/20 text-[#B8860B]"}`}>{q.tingkat || "sedang"}</span> {dup(q.options) && <span className="text-xs text-[#D9381E] inline-flex items-center gap-1 ml-1"><AlertTriangle className="h-3 w-3" /> opsi ganda</span>}</p>
                    <p className="text-sm text-[#4A6B53] mt-1">✓ {q.options[q.answer_index]}</p>
                    <p className="text-xs text-[#5C5F5C] mt-1">{q.pembahasan}</p>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button onClick={() => startEdit(q)} data-testid={`edit-q-${q.id}`} className="p-2 rounded-full hover:bg-[#1A1C1A]/5 text-[#5C5F5C]"><Pencil className="h-4 w-4" /></button>
                    <button onClick={() => del(q.id)} data-testid={`del-q-${q.id}`} className="p-2 rounded-full hover:bg-[#D9381E]/10 text-[#5C5F5C] hover:text-[#D9381E]"><Trash2 className="h-4 w-4" /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </AppShell>
  );
}
